import { memo } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { merge } from '@/components';
import { Button, DatePicker, Menu, MenuItem, Popover, Text } from '@/components/core';
import { CalendarIcon } from '@/components/icons';
import { DateColumnFilter, DateConditions, useInboxStore } from '@/store';
import { generateId } from '@/utils';

import { FilterCard, FilterCardSubType } from './FilterCard.tsx';

export type DateFilterCardProps = FilterCardSubType & {
  columns: string[];
  filter: DateColumnFilter;
};

const IDateFilterCard = ({ columns, filter, className, ...rest }: DateFilterCardProps) => {
  const updateFilter = useInboxStore((state) => state.updateFilter);
  const deleteFilter = useInboxStore((state) => state.deleteFilter);

  return (
    <FilterCard onDelete={() => deleteFilter(filter.id)} className={merge('', className)} {...rest}>
      <MenuTrigger>
        <Button variant="plain">
          {filter.columnName ? (
            <>
              <CalendarIcon />
              <Text>{filter.columnName}</Text>
            </>
          ) : (
            <Text>Select column</Text>
          )}
        </Button>
        <Popover>
          <Menu>
            {columns
              .filter((c) => c !== filter.columnName)
              .filter((c) => ['createdTime', 'updatedTime'].includes(c))
              .map((c) => (
                <MenuItem key={c} onAction={() => updateFilter(filter.id, { columnName: c })}>
                  <Text>{c}</Text>
                </MenuItem>
              ))}
          </Menu>
        </Popover>
      </MenuTrigger>
      <MenuTrigger>
        <Button variant="plain">
          {filter.condition ? <Text>{filter.condition}</Text> : <Text>Select condition</Text>}
        </Button>
        <Popover>
          <Menu>
            {DateConditions.map((condition) => (
              <MenuItem key={condition} onAction={() => updateFilter(filter.id, { condition })}>
                <Text>{condition}</Text>
              </MenuItem>
            ))}
          </Menu>
        </Popover>
      </MenuTrigger>
      <DatePicker
        label={filter.columnName ?? generateId()}
        date={filter.value}
        onDateChange={(value) => updateFilter(filter.id, { value })}
      />
    </FilterCard>
  );
};

export const DateFilterCard = memo(IDateFilterCard) as typeof IDateFilterCard;
